import React, { useCallback, useEffect, useMemo, useState } from "react";
import { useSearchParams } from "react-router-dom";
import axios from "axios";
import { toast } from "sonner";
import {
  AlertTriangle,
  Loader2,
  Download,
  X,
  Search,
  History,
  ShieldAlert,
  ShieldCheck,
  RefreshCw,
  Ban,
} from "lucide-react";
import { Card, CardContent, CardHeader, CardTitle } from "../components/ui/card";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Badge } from "../components/ui/badge";
import {
  ResponsiveContainer, AreaChart, Area, Tooltip as ChartTooltip, XAxis, YAxis,
} from "recharts";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "../components/ui/table";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
  DialogFooter,
} from "../components/ui/dialog";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

function formatTimestamp(v) {
  if (!v) return "—";
  try {
    return new Date(v).toLocaleString();
  } catch {
    return String(v);
  }
}

/**
 * BehavioralSpamView
 *   Renders the caller-grouped Behavioral Spam list. A caller is flagged when
 *   they either:
 *     • hit 2+ distinct offers at any point in time, OR
 *     • hit 10+ calls in any rolling 5-minute window.
 *   Row click opens the Spam Detail modal (full journey).
 */
export default function BehavioralSpamView({ startDate: propStart, endDate: propEnd } = {}) {
  const [searchParams] = useSearchParams();
  // Prefer props (passed from CallAnalysisPage which owns the DateFilter state)
  // — this ensures live picker changes flow into this view. Fallback to URL
  // params so a direct link like /call-analysis?category=behavioral_spam&start_date=...
  // still scopes correctly.
  const urlStart = propStart || searchParams.get("start_date") || "";
  const urlEnd = propEnd || searchParams.get("end_date") || "";
  const [data, setData] = useState(null);
  const [trend, setTrend] = useState(null);
  const [loading, setLoading] = useState(false);
  const [search, setSearch] = useState("");
  const [journeyCaller, setJourneyCaller] = useState(null);
  const [journey, setJourney] = useState(null);
  const [journeyLoading, setJourneyLoading] = useState(false);
  const [exporting, setExporting] = useState(false);
  const [blockingCaller, setBlockingCaller] = useState(null); // caller_number currently being blocked
  const [markingValid, setMarkingValid] = useState(null); // caller_number currently being marked valid
  const [refreshingRow, setRefreshingRow] = useState(null); // per-row Ringba-status re-sync
  const [bulkBlockOpen, setBulkBlockOpen] = useState(false);
  const [bulkBlocking, setBulkBlocking] = useState(false);

  const fetchList = useCallback(async () => {
    setLoading(true);
    try {
      // Honor the date filter that the Dashboard row (or a bookmarked URL)
      // carried through — same query keys as `/dashboard/stats` so the list
      // shown here always matches the count you clicked from.
      const listParams = new URLSearchParams({ limit: "500" });
      if (urlStart) listParams.set("start_date", urlStart);
      if (urlEnd) listParams.set("end_date", urlEnd);
      const [listRes, trendRes] = await Promise.all([
        axios.get(`${API}/calls/behavioral-spam?${listParams.toString()}`),
        axios.get(`${API}/calls/behavioral-spam-trend?days=30`).catch(() => ({ data: null })),
      ]);
      setData(listRes.data);
      setTrend(trendRes.data);
    } catch (e) {
      toast.error(e.response?.data?.detail || "Failed to load behavioral spam list");
    } finally {
      setLoading(false);
    }
  }, [urlStart, urlEnd]);

  useEffect(() => {
    fetchList();
  }, [fetchList]);

  const openJourney = useCallback(async (callerNumber) => {
    setJourneyCaller(callerNumber);
    setJourney(null);
    setJourneyLoading(true);
    try {
      const res = await axios.get(
        `${API}/calls/caller-journey/${encodeURIComponent(callerNumber)}`
      );
      setJourney(res.data);
    } catch (e) {
      toast.error(e.response?.data?.detail || "Failed to load caller journey");
      setJourneyCaller(null);
    } finally {
      setJourneyLoading(false);
    }
  }, []);

  const downloadCsv = useCallback(async () => {
    setExporting(true);
    try {
      const csvParams = new URLSearchParams({ limit: "5000" });
      if (urlStart) csvParams.set("start_date", urlStart);
      if (urlEnd) csvParams.set("end_date", urlEnd);
      const res = await axios.get(
        `${API}/calls/behavioral-spam.csv?${csvParams.toString()}`,
        { responseType: "blob" }
      );
      const blob = new Blob([res.data], { type: "text/csv;charset=utf-8;" });
      const link = document.createElement("a");
      link.href = URL.createObjectURL(blob);
      link.download = `behavioral_spam_${new Date().toISOString().slice(0, 10)}.csv`;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      toast.success("CSV downloaded");
    } catch (e) {
      toast.error("CSV export failed");
    } finally {
      setExporting(false);
    }
  }, [urlStart, urlEnd]);

  // Per-caller "Download Spam Report" — exports THIS caller's full journey
  // (every offer/publisher/timestamp) rather than the whole flagged list.
  const downloadJourneyCsv = useCallback(() => {
    if (!journey?.calls?.length) {
      toast.warning("Nothing to export — this caller has no journey rows.");
      return;
    }
    const headers = [
      "Caller ID", "Timestamp", "Offer", "Publisher", "Buyer",
      "DID Number", "Category", "Duration (sec)", "Call ID",
    ];
    const escape = (v) => {
      if (v === null || v === undefined) return "";
      return `"${String(v).replace(/"/g, '""')}"`;
    };
    const rows = journey.calls.map((c) => [
      journey.caller_number,
      c.call_timestamp || "",
      c.offer_name || c.campaign_name || "",
      c.publisher_name || "",
      c.buyer_name || "",
      c.did_number || "",
      c.category || "",
      c.call_duration ?? "",
      c.id || "",
    ]);
    const csv = [headers.map(escape).join(","), ...rows.map((r) => r.map(escape).join(","))].join("\n");
    const blob = new Blob([csv], { type: "text/csv;charset=utf-8;" });
    const link = document.createElement("a");
    link.href = URL.createObjectURL(blob);
    link.download = `spam_journey_${journey.caller_number.replace(/[^0-9+]/g, "")}_${new Date().toISOString().slice(0, 10)}.csv`;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    toast.success(`Exported ${journey.calls.length} calls for ${journey.caller_number}`);
  }, [journey]);

  // One-Click Block — reuses the same endpoint the Callers page uses so
  // Ringba sync + retry-on-failure semantics are inherited for free.
  const blockCaller = useCallback(async (callerNumber) => {
    setBlockingCaller(callerNumber);
    try {
      const res = await axios.post(`${API}/callers/${encodeURIComponent(callerNumber)}/block`);
      const rb = res.data?.ringba_status || "pending";
      if (rb === "success") {
        toast.success(`${callerNumber} blocked on Ringba`);
      } else if (rb === "queued") {
        toast.info(`${callerNumber} queued for block — Ringba will retry`);
      } else {
        toast.warning(`${callerNumber} blocked locally — Ringba sync deferred`);
      }
      // Optimistic UI update — flip status locally so the button switches
      // straight to "BLOCKED" without waiting for a full refetch.
      setData((cur) => {
        if (!cur) return cur;
        return {
          ...cur,
          blocked_count: (cur.blocked_count || 0) + 1,
          callers: cur.callers.map((c) =>
            c.caller_number === callerNumber
              ? { ...c, ringba_status: "blocked", ringba_synced: rb === "success", blocked_at: new Date().toISOString() }
              : c
          ),
        };
      });
    } catch (e) {
      toast.error(e.response?.data?.detail || "Block failed");
    } finally {
      setBlockingCaller(null);
    }
  }, []);

  // Mark a caller as Valid — whitelists them from Behavioral Spam and flags
  // every historical call as manual_valid so Fake Calls / Adult / Non-US
  // counters clear immediately. Row is removed from the local list on success.
  const markCallerValid = useCallback(async (callerNumber) => {
    setMarkingValid(callerNumber);
    try {
      const res = await axios.post(`${API}/callers/${encodeURIComponent(callerNumber)}/mark-valid`);
      const updated = res.data?.calls_updated ?? 0;
      toast.success(`${callerNumber} marked valid — ${updated} call${updated === 1 ? "" : "s"} cleared`);
      setData((cur) => {
        if (!cur) return cur;
        return {
          ...cur,
          count: Math.max(0, (cur.count || 0) - 1),
          callers: cur.callers.filter((c) => c.caller_number !== callerNumber),
        };
      });
    } catch (e) {
      toast.error(e.response?.data?.detail || "Mark valid failed");
    } finally {
      setMarkingValid(null);
    }
  }, []);

  // Per-row Ringba-status refresh — hits GET /api/callers/{id} (the same
  // read that the standard call rows use) and folds the fresh state back
  // into the local list without a full page reload.
  const refreshRingbaStatus = useCallback(async (callerNumber) => {
    setRefreshingRow(callerNumber);
    try {
      const res = await axios.get(`${API}/callers/${encodeURIComponent(callerNumber)}`);
      const rec = res.data || {};
      setData((cur) => {
        if (!cur) return cur;
        return {
          ...cur,
          callers: cur.callers.map((c) =>
            c.caller_number === callerNumber
              ? {
                  ...c,
                  ringba_status: rec.status === "blocked" ? "blocked" : "active",
                  ringba_synced: !!rec.ringba_synced,
                  blocked_at: rec.blocked_at || c.blocked_at,
                }
              : c
          ),
        };
      });
      toast.success(`Ringba status refreshed for ${callerNumber}`);
    } catch (e) {
      toast.error(e.response?.data?.detail || "Refresh failed");
    } finally {
      setRefreshingRow(null);
    }
  }, []);
  // List of currently-active (unblocked) flagged caller numbers — powers
  // the "Block All Flagged" button state and payload.
  const activeUnblockedNumbers = useMemo(() => {
    return (data?.callers || [])
      .filter((c) => c.ringba_status !== "blocked" && c.caller_number)
      .map((c) => c.caller_number);
  }, [data]);

  const bulkBlockAll = useCallback(async () => {
    if (!activeUnblockedNumbers.length) {
      toast.info("Nothing to block — every flagged caller is already blocked.");
      setBulkBlockOpen(false);
      return;
    }
    setBulkBlocking(true);
    try {
      const res = await axios.post(`${API}/callers/bulk-block`, {
        caller_numbers: activeUnblockedNumbers,
      });
      const { success = 0, queued = 0, failed = 0, total = 0 } = res.data || {};
      if (failed) {
        toast.warning(`Blocked ${success}/${total} · ${queued} queued · ${failed} failed`);
      } else if (queued) {
        toast.success(`Blocked ${success}/${total} · ${queued} queued for retry`);
      } else {
        toast.success(`Blocked all ${total} flagged callers`);
      }
      // Optimistic UI — flip every targeted row to blocked regardless of
      // per-call Ringba status, matching what the DB now reflects. The retry
      // job will finish any queued/failed ones.
      const nowIso = new Date().toISOString();
      setData((cur) => {
        if (!cur) return cur;
        const blockedSet = new Set(activeUnblockedNumbers);
        return {
          ...cur,
          blocked_count: (cur.blocked_count || 0) + activeUnblockedNumbers.length,
          callers: cur.callers.map((c) =>
            blockedSet.has(c.caller_number)
              ? { ...c, ringba_status: "blocked", ringba_synced: true, blocked_at: nowIso }
              : c
          ),
        };
      });
      setBulkBlockOpen(false);
    } catch (e) {
      toast.error(e.response?.data?.detail || "Bulk block failed");
    } finally {
      setBulkBlocking(false);
    }
  }, [activeUnblockedNumbers]);
  const filtered = useMemo(() => {
    if (!data?.callers) return [];
    if (!search.trim()) return data.callers;
    const q = search.trim().toLowerCase();
    return data.callers.filter(
      (c) =>
        c.caller_number.toLowerCase().includes(q) ||
        c.distinct_offers.some((o) => o.toLowerCase().includes(q)) ||
        c.distinct_publishers.some((p) => p.toLowerCase().includes(q))
    );
  }, [data, search]);

  // 30-day trend delta — last 7 days vs previous 7 days. Powers the small
  // "trending up / flat / down" hint next to the sparkline.
  const trendDelta = useMemo(() => {
    if (!trend?.trend?.length || trend.trend.length < 14) return null;
    const arr = trend.trend;
    const lastWeek = arr.slice(-7).reduce((s, d) => s + d.count, 0);
    const prevWeek = arr.slice(-14, -7).reduce((s, d) => s + d.count, 0);
    if (prevWeek === 0 && lastWeek === 0) return { pct: 0, dir: "flat", lastWeek, prevWeek };
    if (prevWeek === 0) return { pct: 100, dir: "up", lastWeek, prevWeek };
    const pct = Math.round(((lastWeek - prevWeek) / prevWeek) * 100);
    const dir = pct > 5 ? "up" : pct < -5 ? "down" : "flat";
    return { pct, dir, lastWeek, prevWeek };
  }, [trend]);

  return (
    <>
      {/* 30-day trend sparkline — shows if behavioral spam volume is climbing */}
      {trend?.trend?.length > 0 && (
        <Card className="mb-4" data-testid="behavioral-spam-trend-card">
          <CardContent className="p-4">
            <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
              <div className="flex items-center gap-4">
                <div>
                  <div className="text-[10px] uppercase tracking-wider text-muted-foreground">30-Day Activity</div>
                  <div className="text-lg font-semibold mt-0.5">
                    Active spammers per day
                  </div>
                  <div className="text-[11px] text-slate-500 mt-0.5">
                    Always shows a 30-day overview, independent of the list filter below.
                  </div>
                </div>
                {trendDelta && (
                  <div className="flex flex-col">
                    <div className="text-[10px] uppercase tracking-wider text-muted-foreground">Week over Week</div>
                    <div
                      className={`text-sm font-semibold mt-0.5 ${
                        trendDelta.dir === "up"
                          ? "text-rose-400"
                          : trendDelta.dir === "down"
                          ? "text-emerald-400"
                          : "text-slate-400"
                      }`}
                      data-testid="trend-delta"
                    >
                      {trendDelta.dir === "up" ? "▲" : trendDelta.dir === "down" ? "▼" : "▬"}{" "}
                      {trendDelta.pct > 0 ? "+" : ""}
                      {trendDelta.pct}% ({trendDelta.prevWeek} → {trendDelta.lastWeek})
                    </div>
                  </div>
                )}
              </div>
            </div>
            <div className="h-24 mt-3">
              <ResponsiveContainer width="100%" height="100%">
                <AreaChart data={trend.trend} margin={{ top: 4, right: 8, left: 0, bottom: 0 }}>
                  <defs>
                    <linearGradient id="spamTrendFill" x1="0" y1="0" x2="0" y2="1">
                      <stop offset="0%" stopColor="#f43f5e" stopOpacity={0.6} />
                      <stop offset="100%" stopColor="#f43f5e" stopOpacity={0.05} />
                    </linearGradient>
                  </defs>
                  <XAxis
                    dataKey="date"
                    hide
                  />
                  <YAxis hide />
                  <ChartTooltip
                    contentStyle={{
                      background: "#11141D",
                      border: "1px solid rgba(255,255,255,0.05)",
                      borderRadius: 8,
                      fontSize: 12,
                      padding: "8px 10px",
                    }}
                    labelStyle={{ color: "#94a3b8", marginBottom: 2 }}
                    itemStyle={{ color: "#fda4af" }}
                    formatter={(value) => [`${value} spammer${value === 1 ? "" : "s"}`, "Active"]}
                    labelFormatter={(v) => new Date(v).toLocaleDateString(undefined, { weekday: "short", month: "short", day: "numeric" })}
                  />
                  <Area
                    type="monotone"
                    dataKey="count"
                    stroke="#f43f5e"
                    strokeWidth={2}
                    fill="url(#spamTrendFill)"
                    isAnimationActive={false}
                  />
                </AreaChart>
              </ResponsiveContainer>
            </div>
          </CardContent>
        </Card>
      )}

      <Card data-testid="behavioral-spam-view">
        <CardHeader className="pb-3">
          <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
            <div>
              <CardTitle className="text-base font-semibold flex items-center gap-2">
                <ShieldAlert className="h-4 w-4 text-rose-400" />
                Behavioral Spam
                {data && (
                  <>
                    <Badge className="bg-rose-500/10 text-rose-300 border-rose-500/30 text-[10px]">
                      {data.count} caller{data.count === 1 ? "" : "s"} flagged
                    </Badge>
                    {(data.blocked_count ?? 0) > 0 && (
                      <Badge className="bg-red-500/10 text-red-300 border-red-500/30 text-[10px]">
                        <Ban className="h-3 w-3 mr-1" /> {data.blocked_count} already blocked
                      </Badge>
                    )}
                  </>
                )}
              </CardTitle>
              <p className="text-xs text-muted-foreground mt-1">
                Flags callers with 2+ distinct offers ever, OR 10+ calls in any 5-min window. Click any row for the full journey.
              </p>
              {(urlStart || urlEnd) && (
                <p className="text-[10px] text-muted-foreground mt-1" data-testid="spam-date-scope">
                  List scoped to <span className="text-slate-300">{(urlStart || "…").slice(0, 10)}</span> → <span className="text-slate-300">{(urlEnd || "…").slice(0, 10)}</span> (Global Date Filter)
                </p>
              )}
            </div>
            <div className="flex items-center gap-2 flex-wrap">
              <Button
                size="sm"
                variant="destructive"
                onClick={() => setBulkBlockOpen(true)}
                disabled={loading || !activeUnblockedNumbers.length}
                data-testid="behavioral-spam-bulk-block-btn"
              >
                <Ban className="h-3.5 w-3.5 mr-1.5" />
                Block All Flagged {activeUnblockedNumbers.length ? `(${activeUnblockedNumbers.length})` : ""}
              </Button>
              <Button
                size="sm"
                variant="outline"
                onClick={fetchList}
                disabled={loading}
                data-testid="behavioral-spam-refresh"
              >
                {loading ? (
                  <Loader2 className="h-3.5 w-3.5 mr-1.5 animate-spin" />
                ) : (
                  <RefreshCw className="h-3.5 w-3.5 mr-1.5" />
                )}
                Refresh
              </Button>
              <Button
                size="sm"
                onClick={downloadCsv}
                disabled={exporting || !filtered.length}
                data-testid="behavioral-spam-export-csv"
              >
                {exporting ? (
                  <Loader2 className="h-3.5 w-3.5 mr-1.5 animate-spin" />
                ) : (
                  <Download className="h-3.5 w-3.5 mr-1.5" />
                )}
                Download CSV
              </Button>
            </div>
          </div>
          <div className="relative mt-3 max-w-sm">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
            <Input
              placeholder="Search caller, offer, or publisher…"
              className="pl-9"
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              data-testid="behavioral-spam-search"
            />
          </div>
        </CardHeader>
        <CardContent className="p-0 overflow-x-auto">
          <Table>
            <TableHeader>
              <TableRow className="border-b border-white/5">
                <TableHead className="text-xs uppercase tracking-wider text-slate-500">Caller ID</TableHead>
                <TableHead className="text-xs uppercase tracking-wider text-slate-500 text-right">Attempts</TableHead>
                <TableHead className="text-xs uppercase tracking-wider text-slate-500 text-right">Offers</TableHead>
                <TableHead className="text-xs uppercase tracking-wider text-slate-500 text-right hidden sm:table-cell">Publishers</TableHead>
                <TableHead className="text-xs uppercase tracking-wider text-slate-500 hidden lg:table-cell">Top Offers</TableHead>
                <TableHead className="text-xs uppercase tracking-wider text-slate-500">Flagged For</TableHead>
                <TableHead className="text-xs uppercase tracking-wider text-slate-500 hidden md:table-cell">Last Seen</TableHead>
                <TableHead className="text-xs uppercase tracking-wider text-slate-500 text-right">Actions</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {loading && !data ? (
                <TableRow>
                  <TableCell colSpan={8} className="text-center py-10 text-muted-foreground">
                    <Loader2 className="h-5 w-5 mr-2 inline animate-spin" /> Scanning callers for behavioral spam…
                  </TableCell>
                </TableRow>
              ) : filtered.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={8} className="text-center py-10 text-muted-foreground">
                    No behavioral-spam callers found.
                  </TableCell>
                </TableRow>
              ) : (
                filtered.map((c) => (
                  <TableRow
                    key={c.caller_number}
                    className="cursor-pointer hover:bg-white/[0.03] transition-colors"
                    onClick={() => openJourney(c.caller_number)}
                    data-testid={`spam-row-${c.caller_number}`}
                  >
                    <TableCell className="font-mono text-sm">{c.caller_number}</TableCell>
                    <TableCell className="text-right font-semibold">{c.total_attempts}</TableCell>
                    <TableCell className="text-right">
                      <span className="text-rose-400 font-semibold">{c.distinct_offer_count}</span>
                    </TableCell>
                    <TableCell className="text-right hidden sm:table-cell">{c.distinct_publisher_count}</TableCell>
                    <TableCell className="text-xs text-slate-400 hidden lg:table-cell max-w-[280px] truncate">
                      {c.distinct_offers.slice(0, 3).join(" · ")}
                      {c.distinct_offers.length > 3 ? ` +${c.distinct_offers.length - 3} more` : ""}
                    </TableCell>
                    <TableCell>
                      <div className="flex flex-wrap gap-1">
                        {c.match_cross_offer && (
                          <Badge className="bg-rose-500/10 text-rose-300 border-rose-500/30 text-[10px]">
                            Cross-Offer
                          </Badge>
                        )}
                        {c.match_burst && (
                          <Badge className="bg-orange-500/10 text-orange-300 border-orange-500/30 text-[10px]">
                            Burst {c.burst_size}
                          </Badge>
                        )}
                      </div>
                    </TableCell>
                    <TableCell className="text-xs text-muted-foreground hidden md:table-cell whitespace-nowrap">
                      {formatTimestamp(c.last_seen)}
                    </TableCell>
                    <TableCell className="text-right" onClick={(e) => e.stopPropagation()}>
                      <div className="flex items-center justify-end gap-1">
                        {/* Shield — Mark as Valid (whitelist caller + clear their calls) */}
                        <Button
                          variant="ghost"
                          size="sm"
                          onClick={() => markCallerValid(c.caller_number)}
                          disabled={markingValid === c.caller_number || blockingCaller === c.caller_number}
                          className="text-muted-foreground hover:text-emerald-400 hover:bg-emerald-500/10"
                          title="Mark as Valid"
                          data-testid={`valid-btn-${c.caller_number}`}
                        >
                          {markingValid === c.caller_number ? (
                            <Loader2 className="h-4 w-4 animate-spin" />
                          ) : (
                            <ShieldCheck className="h-4 w-4" />
                          )}
                        </Button>

                        {/* Ban — Block on Ringba (highlights red when already blocked, disabled) */}
                        <Button
                          variant="ghost"
                          size="sm"
                          onClick={() => c.ringba_status !== "blocked" && blockCaller(c.caller_number)}
                          disabled={c.ringba_status === "blocked" || blockingCaller === c.caller_number || markingValid === c.caller_number}
                          className={
                            c.ringba_status === "blocked"
                              ? "text-red-500 cursor-not-allowed"
                              : "text-muted-foreground hover:text-red-400 hover:bg-red-500/10"
                          }
                          title={
                            c.ringba_status === "blocked"
                              ? (c.blocked_at ? `Already blocked on ${new Date(c.blocked_at).toLocaleString()}` : "Already blocked on Ringba")
                              : "Block on Ringba"
                          }
                          data-testid={`block-btn-${c.caller_number}`}
                        >
                          <Ban className={`h-4 w-4 ${blockingCaller === c.caller_number ? "animate-pulse" : ""}`} />
                        </Button>

                        {/* View Report — opens the Spam Journey modal */}
                        <Button
                          variant="ghost"
                          size="sm"
                          onClick={() => openJourney(c.caller_number)}
                          data-testid={`view-report-${c.caller_number}`}
                        >
                          View Report
                        </Button>

                        {/* Refresh — re-sync live Ringba status for this row */}
                        <Button
                          variant="ghost"
                          size="icon"
                          onClick={() => refreshRingbaStatus(c.caller_number)}
                          disabled={refreshingRow === c.caller_number}
                          title="Refresh Ringba status"
                          data-testid={`refresh-btn-${c.caller_number}`}
                        >
                          <RefreshCw className={`h-4 w-4 ${refreshingRow === c.caller_number ? "animate-spin" : ""}`} />
                        </Button>
                      </div>
                    </TableCell>
                  </TableRow>
                ))
              )}
            </TableBody>
          </Table>
        </CardContent>
      </Card>

      {/* Caller Journey Modal */}
      <Dialog open={!!journeyCaller} onOpenChange={(o) => !o && setJourneyCaller(null)}>
        <DialogContent className="max-w-4xl max-h-[85vh] overflow-hidden flex flex-col" data-testid="caller-journey-modal">
          <DialogHeader>
            <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-2">
              <div>
                <DialogTitle className="flex items-center gap-2">
                  <History className="h-5 w-5 text-rose-400" /> Spam Detail — {journeyCaller}
                </DialogTitle>
                <DialogDescription>
                  Every call this number has ever made in your system, newest first.
                </DialogDescription>
              </div>
              <Button
                size="sm"
                onClick={downloadJourneyCsv}
                disabled={journeyLoading || !journey?.calls?.length}
                data-testid="download-spam-report-btn"
              >
                <Download className="h-3.5 w-3.5 mr-1.5" />
                Download Spam Report
              </Button>
            </div>
          </DialogHeader>
          {journeyLoading ? (
            <div className="flex items-center justify-center py-16">
              <Loader2 className="h-6 w-6 animate-spin text-muted-foreground" />
            </div>
          ) : journey ? (
            <div className="overflow-y-auto flex-1 space-y-4">
              <div className="grid grid-cols-3 gap-3 text-sm">
                <div className="rounded-lg border border-white/10 bg-white/[0.02] p-3">
                  <div className="text-[10px] uppercase tracking-wider text-muted-foreground">Total Calls</div>
                  <div className="text-lg font-semibold mt-1">{journey.total_calls}</div>
                </div>
                <div className="rounded-lg border border-rose-500/20 bg-rose-500/5 p-3">
                  <div className="text-[10px] uppercase tracking-wider text-muted-foreground">Distinct Offers</div>
                  <div className="text-lg font-semibold mt-1 text-rose-300">{journey.distinct_offers.length}</div>
                </div>
                <div className="rounded-lg border border-white/10 bg-white/[0.02] p-3">
                  <div className="text-[10px] uppercase tracking-wider text-muted-foreground">Distinct Publishers</div>
                  <div className="text-lg font-semibold mt-1">{journey.distinct_publishers.length}</div>
                </div>
              </div>
              <div className="rounded-lg border border-white/10 overflow-hidden">
                <Table>
                  <TableHeader>
                    <TableRow className="border-b border-white/5">
                      <TableHead className="text-xs uppercase tracking-wider text-slate-500">Timestamp</TableHead>
                      <TableHead className="text-xs uppercase tracking-wider text-slate-500">Offer</TableHead>
                      <TableHead className="text-xs uppercase tracking-wider text-slate-500">Publisher</TableHead>
                      <TableHead className="text-xs uppercase tracking-wider text-slate-500">Buyer</TableHead>
                      <TableHead className="text-xs uppercase tracking-wider text-slate-500">Category</TableHead>
                      <TableHead className="text-xs uppercase tracking-wider text-slate-500 text-right">Duration</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {journey.calls.map((call) => (
                      <TableRow key={call.id} data-testid={`journey-call-${call.id}`}>
                        <TableCell className="text-xs whitespace-nowrap">{formatTimestamp(call.call_timestamp)}</TableCell>
                        <TableCell className="text-xs max-w-[180px] truncate" title={call.offer_name || call.campaign_name || ""}>
                          {call.offer_name || call.campaign_name || "—"}
                        </TableCell>
                        <TableCell className="text-xs max-w-[140px] truncate" title={call.publisher_name || ""}>
                          {call.publisher_name || "—"}
                        </TableCell>
                        <TableCell className="text-xs max-w-[140px] truncate" title={call.buyer_name || ""}>
                          {call.buyer_name || "—"}
                        </TableCell>
                        <TableCell className="text-xs">
                          <Badge variant="outline" className="text-[10px] px-1.5 py-0">
                            {(call.category || "—").replace(/_/g, " ")}
                          </Badge>
                        </TableCell>
                        <TableCell className="text-xs text-right">
                          {call.call_duration ? `${call.call_duration}s` : "—"}
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </div>
            </div>
          ) : null}
        </DialogContent>
      </Dialog>

      {/* Bulk Block All Flagged confirmation */}
      <Dialog open={bulkBlockOpen} onOpenChange={(o) => !bulkBlocking && setBulkBlockOpen(o)}>
        <DialogContent data-testid="bulk-block-modal">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Ban className="h-5 w-5 text-red-400" /> Block all flagged callers?
            </DialogTitle>
            <DialogDescription>
              This will push <strong>{activeUnblockedNumbers.length}</strong> caller
              number{activeUnblockedNumbers.length === 1 ? "" : "s"} to your Ringba
              blacklist in one shot. Local blocks apply immediately; any Ringba syncs
              that timeout are queued for automatic retry. Already-blocked callers
              are skipped.
            </DialogDescription>
          </DialogHeader>
          <DialogFooter>
            <Button variant="outline" onClick={() => setBulkBlockOpen(false)} disabled={bulkBlocking}>
              Cancel
            </Button>
            <Button
              variant="destructive"
              onClick={bulkBlockAll}
              disabled={bulkBlocking}
              data-testid="bulk-block-confirm-btn"
            >
              {bulkBlocking ? <Loader2 className="h-4 w-4 mr-2 animate-spin" /> : <Ban className="h-4 w-4 mr-2" />}
              Block {activeUnblockedNumbers.length} Callers
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </>
  );
}
